import React, { useState, useEffect } from 'react';
import { fetchAPI } from '../../../services/apiClient';
import { formatTimeCR, fechaLocalYMD } from '../../../utils/date';

interface SinpeValidacion {
  id: string;
  ticket_id: string | null;
  ticket_number: string | null;
  monto: number;
  fecha: string;
  vendedor_id: string;
  vendedor_nombre: string | null;
  validado: boolean;
  validado_por: string | null;
  validado_at: string | null;
  notas: string | null;
  created_at: string;
  ticket_created_at: string | null;
  cliente_nombre: string | null;
  referencia: string | null;
  metodo_pago: string | null;
  tipo_origen: 'TICKET' | 'ABONO_CREDITO';
  cliente_id: string | null;
  abono_id: string | null;
}

interface SinpeResumen {
  total_registrado: number;
  total_validado: number;
  total_pendiente: number;
  cantidad_validadas: number;
  cantidad_pendientes: number;
}

export const LocalValidacionSinpePage: React.FC = () => {
  const [fecha, setFecha] = useState<string>(fechaLocalYMD());
  const [validaciones, setValidaciones] = useState<SinpeValidacion[]>([]);
  const [resumen, setResumen] = useState<SinpeResumen | null>(null);
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    cargarDatos();
  }, [fecha, soloPendientes]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [validacionesData, resumenData] = await Promise.all([
        fetchAPI<SinpeValidacion[]>(`/operaciones/sinpe/validaciones?fecha=${fecha}&solo_pendientes=${soloPendientes}`),
        fetchAPI<SinpeResumen>(`/operaciones/sinpe/resumen?fecha=${fecha}`)
      ]);
      setValidaciones(validacionesData);
      setResumen(resumenData);
    } catch (error: any) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleValidacion = async (validacionId: string, validado: boolean) => {
    try {
      await fetchAPI(`/operaciones/sinpe/validaciones/${validacionId}`, {
        method: 'PUT',
        body: JSON.stringify({ validado, notas: null })
      });
      await cargarDatos();
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    }
  };

  const formatMonto = (monto: number) => {
    return `₡${monto.toLocaleString('es-CR')}`;
  };

  const formatHora = (fechaStr: string) => {
    try {
      return formatTimeCR(fechaStr);
    } catch {
      return '';
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Validación de SINPE / Transferencia</h1>

      {/* Resumen */}
      {resumen && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 mb-1">Total Registrado</div>
            <div className="text-2xl font-bold text-gray-900">{formatMonto(resumen.total_registrado)}</div>
          </div>
          <div className="bg-green-50 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 mb-1">Validado</div>
            <div className="text-2xl font-bold text-green-700">{formatMonto(resumen.total_validado)}</div>
            <div className="text-xs text-gray-500 mt-1">{resumen.cantidad_validadas} transacciones</div>
          </div>
          <div className="bg-yellow-50 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 mb-1">Pendiente</div>
            <div className="text-2xl font-bold text-yellow-700">{formatMonto(resumen.total_pendiente)}</div>
            <div className="text-xs text-gray-500 mt-1">{resumen.cantidad_pendientes} transacciones</div>
          </div>
        </div>
      )}

      {/* Controles */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha
            </label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center mt-6">
            <input
              type="checkbox"
              id="soloPendientes"
              checked={soloPendientes}
              onChange={(e) => setSoloPendientes(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="soloPendientes" className="ml-2 text-sm text-gray-700">
              Solo pendientes
            </label>
          </div>
        </div>
      </div>

      {/* Nota informativa */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Nota:</strong> La validación de SINPE / Transferencia confirma que el dinero entró al banco.
              Para <strong>abonos de crédito</strong>, al validar se aplica el abono al saldo del cliente.
              Para <strong>tickets</strong>, la validación es informativa y no afecta saldos.
            </p>
          </div>
        </div>
      </div>

      {/* Lista de validaciones */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : validaciones.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No hay transacciones SINPE / Transferencia para esta fecha
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hora
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Origen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acción
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {validaciones.map((v) => (
                  <tr key={v.id} className={v.validado ? 'bg-green-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatHora(v.ticket_created_at || v.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium">
                      {v.tipo_origen === 'ABONO_CREDITO' ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                          Abono crédito
                        </span>
                      ) : (
                        v.ticket_number || (v.ticket_id ? v.ticket_id.substring(0, 12) : '—')
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {v.metodo_pago || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {v.vendedor_nombre || v.vendedor_id.substring(0, 12)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {v.cliente_nombre || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {v.referencia || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {formatMonto(v.monto)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {v.validado ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          ✓ Validado
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                          ⏳ Pendiente
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {v.validado ? (
                        <button
                          onClick={() => toggleValidacion(v.id, false)}
                          className="text-yellow-600 hover:text-yellow-800 font-medium"
                        >
                          Marcar pendiente
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleValidacion(v.id, true)}
                          className="text-green-600 hover:text-green-800 font-medium"
                        >
                          ✓ Validar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocalValidacionSinpePage;
